import React, { useCallback, useContext, useMemo, useState } from 'react';
import * as DOMPurify from 'dompurify';
import { ThemeProvider } from 'styled-components';
import { InformativeNotificationProps } from './types';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import BasicIcon from '../BasicIcon/BasicIcon';
import {
  Container,
  NotificationBody,
  NotificationTitle,
  SimpleNotificationContainer,
} from './styles';
import { BasicIconsType } from '..';

const InformativeNotification: React.FC<InformativeNotificationProps> = (props) => {
  const {
    title,
    body,
    dataTestId,
    status = 'default',
    isMobile,
    width,
    showClearIcon = true,
    onClick,
    onClose,
  } = props;
  const [hidden, setHidden] = useState(false);
  const { actualTheme, isMobile: mobileTheme } = useContext(ThemeContext);
  const useMobileVersion = useMemo(() => (isMobile !== undefined ? isMobile : mobileTheme), [
    isMobile,
    mobileTheme,
  ]);

  const handleClose = useCallback(() => {
    setHidden(true);
    if (onClose) onClose();
  }, [onClose]);

  const getIconName = useCallback((): BasicIconsType => {
    if (status === 'error') return 'x-circle';
    if (status === 'success') return 'check-circle';
    if (status === 'warning') return 'exclamation';
    return 'information-circle';
  }, [status]);

  const handleMessage = useMemo(
    () => ({ __html: DOMPurify.sanitize(body, { USE_PROFILES: { html: true } }) }),
    [body],
  );
  return (
    <ThemeProvider theme={actualTheme}>
      {!hidden && (
        <Container
          width={width}
          id="informative-notification-id"
          data-testid={dataTestId}
          status={status}
          isMobile={useMobileVersion}
          clickable={!!onClick}
          onClick={onClick}
        >
          {title ? (
            <>
              <NotificationTitle isMobile={useMobileVersion}>
                <BasicIcon
                  name={getIconName()}
                  color={actualTheme.informationNotification[status].border}
                  size={useMobileVersion ? 'extra-small' : 'small'}
                />
                <div>{title}</div>
                {showClearIcon && (
                  <BasicIcon
                    name="x"
                    size={useMobileVersion ? 'extra-small' : 'small'}
                    onClick={handleClose}
                  />
                )}
              </NotificationTitle>
              <NotificationBody
                isMobile={useMobileVersion}
                dangerouslySetInnerHTML={handleMessage}
              />
            </>
          ) : (
            <SimpleNotificationContainer>
              <BasicIcon
                name={getIconName()}
                color={actualTheme.informationNotification[status].border}
                size={useMobileVersion ? 'extra-small' : 'small'}
              />
              <NotificationBody
                isMobile={useMobileVersion}
                dangerouslySetInnerHTML={handleMessage}
              />
              {showClearIcon && (
                <BasicIcon
                  name="x"
                  size={useMobileVersion ? 'extra-small' : 'small'}
                  onClick={handleClose}
                />
              )}
            </SimpleNotificationContainer>
          )}
        </Container>
      )}
    </ThemeProvider>
  );
};

export default InformativeNotification;
